import { Card, CardContent } from "../../../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/ui/table";
import { Layers } from "lucide-react";
import { classifySubCategory, computeLotEconomics, weightedDaysXirr } from "./subCategoryClassifier";

/**
 * Section F — Sub-Category Wise Allocation table.
 * Groups the client's active holdings into SEBI sub-categories using a
 * scheme-name regex classifier. Each row: Amount Invested / Current
 * Value / Abs Ret% / XIRR% / Alloc%. Total row Alloc% is 100.00%.
 */
export const SubCategoryAllocationCard = ({ filteredActiveUnits = [] }) => {
  const buckets = {};
  filteredActiveUnits.forEach((txn) => {
    const cat = classifySubCategory(txn.scheme_name);
    if (!buckets[cat]) buckets[cat] = { cost: 0, market: 0, cashflows: [] };
    const { cost, market, dateObj } = computeLotEconomics(txn);
    buckets[cat].cost += cost;
    buckets[cat].market += market;
    if (cost > 0 && dateObj) buckets[cat].cashflows.push({ date: dateObj, amount: -cost });
  });

  const totalCost = Object.values(buckets).reduce((s, b) => s + b.cost, 0);
  const totalMarket = Object.values(buckets).reduce((s, b) => s + b.market, 0);

  const rows = Object.entries(buckets)
    .filter(([, b]) => b.cost > 0 || b.market > 0)
    .map(([cat, b]) => {
      const absRet = b.cost > 0 ? ((b.market - b.cost) / b.cost) * 100 : 0;
      const xirr = weightedDaysXirr(b.cost, b.market, b.cashflows);
      const alloc = totalMarket > 0 ? (b.market / totalMarket) * 100 : 0;
      return { cat, cost: b.cost, market: b.market, absRet, xirr, alloc };
    })
    .sort((a, b) => b.market - a.market);

  return (
    <Card className="border-gray-200" data-testid="summary-subcategory-card">
      <CardContent className="p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <Layers className="w-5 h-5 text-teal-600" />
          SUB-CATEGORY WISE ALLOCATION
        </h3>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="font-semibold">Sub category name</TableHead>
                <TableHead className="font-semibold text-right">Amount Invested</TableHead>
                <TableHead className="font-semibold text-right">Current value</TableHead>
                <TableHead className="font-semibold text-right">Abs Ret%</TableHead>
                <TableHead className="font-semibold text-right">XIRR%</TableHead>
                <TableHead className="font-semibold text-right">Alloc%</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-gray-400 py-6">
                    No active holdings
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {rows.map((r) => (
                    <TableRow
                      key={r.cat}
                      className="hover:bg-gray-50"
                      data-testid={`subcat-row-${r.cat.replace(/\s+/g, "-")}`}
                    >
                      <TableCell className="font-medium">{r.cat}</TableCell>
                      <TableCell className="text-right font-mono">
                        ₹{r.cost.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        ₹{r.market.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                      </TableCell>
                      <TableCell className={`text-right font-mono ${r.absRet >= 0 ? "text-green-600" : "text-red-600"}`}>
                        {r.absRet >= 0 ? "+" : ""}
                        {r.absRet.toFixed(2)}%
                      </TableCell>
                      <TableCell className={`text-right font-mono ${r.xirr >= 0 ? "text-green-600" : "text-red-600"}`}>
                        {r.xirr >= 0 ? "+" : ""}
                        {r.xirr.toFixed(2)}%
                      </TableCell>
                      <TableCell className="text-right font-mono">{r.alloc.toFixed(2)}%</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-gray-100 font-semibold border-t-2">
                    <TableCell className="font-bold">Total</TableCell>
                    <TableCell className="text-right font-mono font-bold">
                      ₹{totalCost.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                    </TableCell>
                    <TableCell className="text-right font-mono font-bold">
                      ₹{totalMarket.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                    </TableCell>
                    <TableCell className={`text-right font-mono font-bold ${(totalMarket - totalCost) >= 0 ? "text-green-600" : "text-red-600"}`}>
                      {totalCost > 0
                        ? `${((totalMarket - totalCost) / totalCost) * 100 >= 0 ? "+" : ""}${(((totalMarket - totalCost) / totalCost) * 100).toFixed(2)}%`
                        : "0.00%"}
                    </TableCell>
                    <TableCell className="text-right font-mono font-bold">-</TableCell>
                    <TableCell className="text-right font-mono font-bold">100.00%</TableCell>
                  </TableRow>
                </>
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};
